import type { FacetSource } from "@/services/facets";
import { cleanQuery, omit } from "@/utils/queryClient";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

export type FacetTuple<V> = readonly [V | null, number | null];
export type FacetsResponse = Record<string, ReadonlyArray<FacetTuple<any>>>;

type FacetValue<T> = T extends ReadonlyArray<FacetTuple<infer V>> ? V : never;

export type FacetCounts<TFacets extends FacetsResponse> = {
	[K in keyof TFacets]: Array<{
		value: FacetValue<TFacets[K]> | null;
		total: number;
		filtered: number;
	}>;
};

const typedKeys = <T extends object>(obj: T) =>
	Object.keys(obj) as Array<keyof T>;

function toCountMap<V>(tuples: ReadonlyArray<FacetTuple<V>>) {
	const m = new Map<V | null, number>();
	for (const [value, count] of tuples) {
		if (count == null) continue;
		m.set(value, count);
	}
	return m;
}

export interface UseFacetsParams<TFacets extends FacetsResponse> {
	source: FacetSource<TFacets>;
	/** Always applied and never excluded — e.g. the current user or a direction. */
	scope?: Record<string, unknown>;
	/** The filters the user picked, keyed by API query param. */
	filters?: Record<string, unknown>;
	/**
	 * Facet key -> the filter param that facet drives, when the two names differ.
	 * A facet is never narrowed by its own filter, so a multi-select keeps showing
	 * its unpicked options.
	 */
	ownParams?: Partial<Record<keyof TFacets & string, string>>;
	enabled?: boolean;
}

/**
 * Facet counts for a whole filter form.
 *
 * Every facet gets counts computed with its own filter left out, which is what
 * makes a multi-select usable: picking `x1` must not zero out `x2`, since no row
 * can be both. Counts for `x` therefore come from a request carrying every
 * filter except `x`.
 *
 * Requests are deduplicated by react-query, so an untouched filter costs nothing.
 */
export function useFacets<TFacets extends FacetsResponse>(
	params: UseFacetsParams<TFacets>,
): FacetCounts<TFacets> {
	const { source, scope, filters, ownParams, enabled = true } = params;

	const scopeQuery = useMemo(() => cleanQuery(scope ?? {}), [scope]);
	const filterQuery = useMemo(() => cleanQuery(filters ?? {}), [filters]);
	const allQuery = useMemo(
		() => ({ ...scopeQuery, ...filterQuery }),
		[scopeQuery, filterQuery],
	);

	const [totalRes, allRes] = useQueries({
		queries: [scopeQuery, allQuery].map((query) => ({
			queryKey: source.queryKey(query),
			queryFn: () => source.fetch(query),
			enabled,
		})),
	});

	const totalFacets = totalRes.data;

	// A facet only needs its own request when it actually has an active filter.
	const ownFilteredParams = useMemo(() => {
		if (!totalFacets) return [] as string[];
		const active = new Set<string>();
		for (const facetKey of Object.keys(totalFacets)) {
			const param = ownParams?.[facetKey] ?? facetKey;
			if (param in filterQuery) active.add(param);
		}
		return Array.from(active).sort();
	}, [totalFacets, ownParams, filterQuery]);

	const withoutOwn = useQueries({
		queries: ownFilteredParams.map((param) => {
			const query = omit(allQuery, [param]);
			return {
				queryKey: source.queryKey(query),
				queryFn: () => source.fetch(query),
				enabled,
			};
		}),
		combine: (results) => results.map((r) => r.data),
	});

	return useMemo(() => {
		const result = {} as FacetCounts<TFacets>;
		if (!totalFacets) return result;

		const byParam = new Map(
			ownFilteredParams.map((param, i) => [param, withoutOwn[i]]),
		);

		for (const facetKey of typedKeys(totalFacets)) {
			type V = FacetValue<TFacets[typeof facetKey]>;

			const param = ownParams?.[facetKey as string] ?? (facetKey as string);
			// While this facet's own request is in flight, fall back to the
			// unfiltered counts rather than the fully filtered ones, so its options
			// never blink to zero.
			const filteredFacets = byParam.has(param)
				? byParam.get(param) ?? (totalFacets as TFacets)
				: allRes.data ?? (totalFacets as TFacets);

			const totalMap = toCountMap<V>(totalFacets[facetKey] as any);
			const filteredMap = toCountMap<V>(
				(filteredFacets[facetKey] ?? []) as any,
			);

			const allValues = new Set<V | null>();
			for (const v of totalMap.keys()) allValues.add(v);
			for (const v of filteredMap.keys()) allValues.add(v);

			result[facetKey] = Array.from(allValues).map((value) => ({
				value,
				total: totalMap.get(value) ?? 0,
				filtered: filteredMap.get(value) ?? 0,
			}));

			// The filtered count is the one the user is choosing between, so it
			// leads the ordering; the total only breaks ties.
			result[facetKey].sort(
				(a, b) => b.filtered - a.filtered || b.total - a.total,
			);
		}

		return result;
	}, [totalFacets, allRes.data, withoutOwn, ownFilteredParams, ownParams]);
}
